import {coachingApi} from 'sources/api';
import {List} from 'immutable';
import uniqBy from 'lodash/uniqBy';
import CoachingReview from 'records/CoachingReview';
import UPCOMING_REVIEW_ROLES from 'actions/coaching/UPCOMING_REVIEW_ROLES';

function upcomingReview(review) {
  return new CoachingReview({
    ...review.nextReviewSummary,
    planId: review.coachingPlan.id,
    todo: review.upcomingReviewTodos,
    otherParticipantTodo: review.otherParticipantTodos,
    coachingPlan: review.coachingPlan,
  });
}

const UpcomingReviewsSource = {
  /**
   * Gather upcoming reviews for the currently logged in user.
   *
   * Managers will receive results where they are the coaching participant, and reps
   * will receive results where they are the recipient.
   *
   * Supports pagination.
   *
   * @param userId - which user to grab upcoming reviews for
   * @param role - Coach, Participant, or both.  if both, we make two requests and combine the lists.
   * @param from - start index
   * @param size - how many to grab
   * @param reviewId - optional specific review id to return in addition to the ones specified
   * @param teamId - optional specific team id, for the "coach" role only, used to restrict the coach's
   * reviews to members of that team
   * @returns Promise - list of upcoming reviews
   */
  async upcomingReviews(userId, role, from, size, reviewId, teamId) {
    const upcomingPromise =
      role !== UPCOMING_REVIEW_ROLES.ALL
        ? coachingApi.getJson('/upcoming-reviews', {
            params: {userId, ...(teamId && {teamId}), role, from, size},
          })
        : // for a coach, get upcoming reviews where they're the coach and the participant
          Promise.all([
            coachingApi.getJson('/upcoming-reviews', {
              params: {
                userId,
                ...(teamId && {teamId}),
                role: UPCOMING_REVIEW_ROLES.COACH,
                from,
                size,
              },
            }),
            coachingApi.getJson('/upcoming-reviews', {
              params: {
                userId,
                ...(teamId && {teamId}),
                role: UPCOMING_REVIEW_ROLES.PARTICIPANT,
                from,
                size,
              },
            }),
            // note that this can potentially give us 2x size, do we care?
            // they do get sorted later in the store
          ]).then(([a, b]) => a.concat(b));

    const reviews = await Promise.all([
      upcomingPromise,
      reviewId &&
        coachingApi.getJson(`/upcoming-reviews/${reviewId}`).catch(e => {
          if (e.response.status === 500) console.error(e);
        }),
    ]);

    const filteredReviews = reviews.flat().filter(x => x);

    return List(uniqBy(filteredReviews, r => r.nextReviewSummary.id).map(upcomingReview));
  },

  sendReminder(reviewId) {
    return coachingApi.postJson(`/upcoming-reviews/${reviewId}/reminder`).then(upcomingReview);
  },
};

export default UpcomingReviewsSource;
